import { useFocusEffect } from "expo-router";
import type React from "react";
import { useCallback, useState } from "react";
import { InteractionManager } from "react-native";

type LazyRenderProps = {
  placeholder?: React.ReactNode;
  children: React.ReactNode;
  delay?: number;
};

/**
 * Defers rendering `children` until after the current interaction queue
 * (including stack transitions) has finished, to avoid jank on push.
 */
const LazyRender = ({ children, placeholder, delay }: LazyRenderProps) => {
  const [ready, setReady] = useState(false);

  useFocusEffect(
    useCallback(() => {
      setReady(false);
      let timeout: NodeJS.Timeout | undefined;
      const task = InteractionManager.runAfterInteractions(() => {
        if (delay) {
          timeout = setTimeout(() => {
            setReady(true);
          }, delay);
        } else {
          setReady(true);
        }
      });
      return () => {
        if (timeout) clearTimeout(timeout);
        task.cancel();
        setReady(false);
      };
    }, [delay]),
  );

  return ready ? children : placeholder;
};

export default LazyRender;
